import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { ListingSortComponentDef } from '@df/catalog-ui/listing/sort/listing-sort.component.def';
import { DfEventService } from '@df/core/service/df-event';
import { GtmService } from '@df/module-gtm/gtm.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs/internal/Subscription';
import { DialogComponent } from '../../../../common/dialog/dialog.component';
import { DIALOG_EVENT, DialogEventType } from '../../../../common/dialog/dialog.interface';
import { DialogOpenDirective } from '../../../../common/dialog/open/dialog-open.directive';
import { OverlayComponent } from '../../../../ui/common/overlay/overlay.component';

@Component({
    selector: 'listing-sort',
    templateUrl: './listing-sort.html',
    styleUrl: './listing-sort.scss',
    standalone: true,
    imports: [CommonModule, TranslatePipe, DialogComponent, DialogOpenDirective, OverlayComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListingSortComponent extends ListingSortComponentDef implements OnInit, OnDestroy {
    readonly dialogId = 'listing-sort-dialog';

    @Output()
    filtersClose = new EventEmitter();

    @Output()
    filtersOpen = new EventEmitter();

    protected subscriptions: Subscription = new Subscription();
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected gtmService = inject(GtmService);
    private dfEventService = inject(DfEventService);

    get selectedLabel() {
        return this.sort.selectionLabel;
    }

    ngOnInit() {
        this.subscriptions.add(this.sort.subscribe(() => this.changeDetectorRef.detectChanges()));

        // Listen for dialog events to emit component events
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === DIALOG_EVENT && event.data.id === this.dialogId) {
                    if (event.data.type === DialogEventType.Close) {
                        this.filtersClose.emit();
                    }
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Open,
                id: this.dialogId
            },
            action: () => {
                this.sendGtmEvent('openSortBy');
                this.filtersOpen.emit();
            }
        });
    }

    handleSortBy(option: IListingSortOption | undefined) {
        this.closeModal();

        this.sort.by(option);
        this.detectChanges();
    }

    closeModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Close,
                id: this.dialogId
            }
        });

        this.sendGtmEvent('closeSortBy');
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }
}
